import React, { useState } from "react";
import PlayerDetails from "./CrmPlayerDetails";
import { useGetPlayersQuery, useGetPlayerByIdQuery } from "../redux/crmApi";

const PlayerPage = () => {
  const [expandedPlayerId, setExpandedPlayerId] = useState(null);
  const [selectedPlayerId, setSelectedPlayerId] = useState(null);
  const [searchTerm, setSearchTerm] = useState(""); // 🔍 Search input
  const [sortBy, setSortBy] = useState("name"); // 🔄 Sorting
  const [sortOrder, setSortOrder] = useState("asc"); // 🔄 Sorting order
  const [currentPage, setCurrentPage] = useState(1);
  const playersPerPage = 10; // 📌 Items per page

  const { data: players = [], isLoading, error } = useGetPlayersQuery();
  const { data: playerDetails } = useGetPlayerByIdQuery(selectedPlayerId, {
    skip: !selectedPlayerId,
  });

  if (isLoading)
    return (
      <div
        style={{ display: "flex", justifyContent: "center", padding: "40px" }}
      >
        <div className="spinner"></div> {/* Add CSS animation */}
        <p>Loading player data...</p>
      </div>
    );
  if (error)
    return (
      <div style={{ color: "red", padding: "20px", textAlign: "center" }}>
        Error loading players: {error.message}
        <button
          onClick={() => window.location.reload()}
          style={{ marginLeft: "10px" }}
        >
          Retry
        </button>
      </div>
    );
  // 🔍 Filter players by name
  const filteredPlayers = players.filter((player) =>
    player.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // 🔄 Sort players based on selected column
  const sortedPlayers = [...filteredPlayers].sort((a, b) => {
    const valA = a[sortBy];
    const valB = b[sortBy];

    if (typeof valA === "string")
      return sortOrder === "asc"
        ? valA.localeCompare(valB)
        : valB.localeCompare(valA);
    return sortOrder === "asc" ? valA - valB : valB - valA;
  });

  // 📌 Pagination: Get current page players
  const indexOfLastPlayer = currentPage * playersPerPage;
  const indexOfFirstPlayer = indexOfLastPlayer - playersPerPage;
  const currentPlayers = sortedPlayers.slice(
    indexOfFirstPlayer,
    indexOfLastPlayer,
  );

  return (
    <div
      style={{
        padding: "20px",
        fontFamily: "Arial, sans-serif",
        backgroundColor: "#f5f5f5",
        minHeight: "100vh",
      }}
    >
      {/* 🔍 Search Input */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <input
          type="text"
          placeholder="Search by name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            padding: "10px",
            borderRadius: "8px",
            border: "1px solid #ddd",
            width: "300px",
            fontSize: "16px",
            outline: "none",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
          }}
        />

        {/* 🔄 Sort Options */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <label style={{ fontSize: "16px", fontWeight: "500" }}>
            Sort by:
          </label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{
              padding: "8px",
              borderRadius: "8px",
              border: "1px solid #ddd",
              fontSize: "16px",
              outline: "none",
              cursor: "pointer",
            }}
          >
            <option value="name">Name</option>
            <option value="score">Score</option>
            <option value="player_id">Player ID</option>
          </select>
          <button
            onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
            style={{
              padding: "8px 16px",
              borderRadius: "8px",
              border: "none",
              backgroundColor: "#50868f",
              color: "#fff",
              fontSize: "16px",
              cursor: "pointer",
              transition: "background-color 0.3s ease",
              ":hover": {
                backgroundColor: "#3a6a73",
              },
            }}
          >
            {sortOrder === "asc" ? "⬆️ Ascending" : "⬇️ Descending"}
          </button>
        </div>
      </div>

      {/* 📋 Table */}
      <div
        style={{
          borderRadius: "8px",
          overflow: "hidden",
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            backgroundColor: "#fff",
          }}
        >
          <thead>
            <tr>
              <th
                style={{
                  backgroundColor: "#50868f",
                  color: "#fff",
                  padding: "16px",
                  textAlign: "left",
                }}
              >
                Player ID
              </th>
              <th
                style={{
                  backgroundColor: "#50868f",
                  color: "#fff",
                  padding: "16px",
                  textAlign: "left",
                }}
              >
                Name
              </th>
              <th
                style={{
                  backgroundColor: "#50868f",
                  color: "#fff",
                  padding: "16px",
                  textAlign: "left",
                }}
              >
                Country Code
              </th>
              <th
                style={{
                  backgroundColor: "#50868f",
                  color: "#fff",
                  padding: "16px",
                  textAlign: "left",
                }}
              >
                Phone
              </th>
              <th
                style={{
                  backgroundColor: "#50868f",
                  color: "#fff",
                  padding: "16px",
                  textAlign: "left",
                }}
              >
                Score
              </th>
              <th
                style={{
                  backgroundColor: "#50868f",
                  color: "#fff",
                  padding: "16px",
                  textAlign: "left",
                }}
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {currentPlayers.map((player) => (
              <React.Fragment key={player.player_id}>
                <tr
                  style={{
                    borderBottom: "1px solid #ddd",
                    transition: "background-color 0.3s ease",
                  }}
                >
                  <td style={{ padding: "16px", textAlign: "left" }}>
                    {player.player_id}
                  </td>
                  <td style={{ padding: "16px", textAlign: "left" }}>
                    {player.name}
                  </td>
                  <td style={{ padding: "16px", textAlign: "left" }}>
                    {player.country_code}
                  </td>
                  <td style={{ padding: "16px", textAlign: "left" }}>
                    {player.phone}
                  </td>
                  <td style={{ padding: "16px", textAlign: "left" }}>
                    {player.score}
                  </td>
                  <td style={{ padding: "16px", textAlign: "left" }}>
                    <button
                      style={{
                        padding: "8px 16px",
                        border: "none",
                        borderRadius: "8px",
                        backgroundColor: "#50868f",
                        color: "#fff",
                        cursor: "pointer",
                        transition: "background-color 0.3s ease",
                        ":hover": {
                          backgroundColor: "#3a6a73",
                        },
                      }}
                      onClick={() =>
                        setExpandedPlayerId(
                          expandedPlayerId === player.player_id
                            ? null
                            : player.player_id,
                        )
                      }
                    >
                      {expandedPlayerId === player.player_id
                        ? "Collapse"
                        : "Expand"}
                    </button>
                  </td>
                </tr>
                {expandedPlayerId === player.player_id && (
                  <tr>
                    <td colSpan="6" style={{ backgroundColor: "#f0f0f0" }}>
                      <div style={{ padding: "20px" }}>
                        <PlayerDetails player={playerDetails || player} />
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* 📌 Pagination */}
      <div style={{ marginTop: "20px", textAlign: "center" }}>
        <button
          onClick={() => setCurrentPage(currentPage - 1)}
          disabled={currentPage === 1}
          style={{
            padding: "8px 16px",
            borderRadius: "8px",
            border: "none",
            backgroundColor: "#50868f",
            color: "#fff",
            cursor: "pointer",
            marginRight: "10px",
            transition: "background-color 0.3s ease",
            ":hover": {
              backgroundColor: "#3a6a73",
            },
          }}
        >
          ◀️ Prev
        </button>
        <span style={{ fontSize: "16px", fontWeight: "500" }}>
          Page {currentPage}
        </span>
        <button
          onClick={() => setCurrentPage(currentPage + 1)}
          disabled={indexOfLastPlayer >= sortedPlayers.length}
          style={{
            padding: "8px 16px",
            borderRadius: "8px",
            border: "none",
            backgroundColor: "#50868f",
            color: "#fff",
            cursor: "pointer",
            marginLeft: "10px",
            transition: "background-color 0.3s ease",
            ":hover": {
              backgroundColor: "#3a6a73",
            },
          }}
        >
          Next ▶️
        </button>
      </div>
    </div>
  );
};

export default PlayerPage;
